/**
 * The theme's STATIC sponsor-banner slots — the single source of truth for the
 * manifest's `adSlots` block (scripts/build-theme-manifest.mjs imports this
 * file) and for the templates that render them (lib/placements/load.ts).
 *
 * Contract: docs/synced/fe-templates-ad-slots-contract.md. The backend cannot
 * see a page's layout, so the theme states where a banner may appear; a portal
 * admin books campaigns against these NAMES, and the public `adDecisions` query
 * fills them per render. Two rules that follow from that:
 *
 * - Declare only slots a template actually renders. A declared slot nobody
 *   renders is one an admin can sell and nobody will ever see.
 * - A slot's name is the booking key. Renaming one orphans every campaign
 *   booked into it (the admin sees `live: false`). Add a new slot and retire
 *   the old name across a version instead.
 *
 * Neutral naming on purpose (this directory, the component, its CSS class):
 * ad blockers filter on substrings like `ad-`, `banner`, `sponsor`, `promo`
 * in URLs and class names, and would hide the element even though the
 * decision already arrived server-side. See the gotchas chapter.
 *
 * No imports — the manifest builder transpiles this file standalone.
 */

/**
 * The backend's `AdFormat` enum as `[width, height]` ratio terms — the BOX a
 * slot of that format reserves (introspected on the dev API, 2026-09-05; the
 * rebuilt IAB flexible portfolio). The ratio is the backend's, mirrored, not
 * derived from the reference size: it boxes the 300×250 medium rectangle at
 * 1:1 on purpose ("so it gets a margin" — a creative is never cropped).
 *
 * HOLD: production's api1 still serves the previous five names (LEADERBOARD,
 * RECTANGLE, SQUARE, HALF_PAGE, MOBILE_BANNER). The production pipeline pushes
 * the manifest there and would be refused `BILLBOARD`, so this table and the
 * catalog below merge only once `npm run schema:check` shows api1 on this enum
 * (roadmap O43).
 */
export const PLACEMENT_FORMATS = {
  MEDIUM_RECTANGLE: [1, 1],
  LANDSCAPE_RECTANGLE: [2, 1],
  BILLBOARD: [4, 1],
  MOBILE_BANNER: [6, 1],
  LEADERBOARD: [8, 1],
  PUSHDOWN: [10, 1],
  HALF_PAGE: [1, 2],
  PORTRAIT: [1, 3],
  SKYSCRAPER: [1, 4],
  FULL_PAGE_VERTICAL: [9, 16],
  LARGE_MOBILE_BANNER: [16, 5],
} as const;

/**
 * The previous enum's names, as page documents saved under it still carry
 * them: both the 6:5 rectangle and the square become the medium rectangle —
 * the backend's 1:1 box IS the square's, and a 6:5 creative sits in it with a
 * margin rather than being cropped. Read-side only: nothing rewrites a saved
 * document; the declaration on the next save and every render use the
 * canonical name.
 */
const LEGACY_FORMATS: Record<string, keyof typeof PLACEMENT_FORMATS> = {
  RECTANGLE: "MEDIUM_RECTANGLE",
  SQUARE: "MEDIUM_RECTANGLE",
};

/** The format's canonical name — its own if current, its successor if legacy,
 *  null for anything else. */
export function canonicalFormat(name: unknown): keyof typeof PLACEMENT_FORMATS | null {
  if (typeof name !== "string") return null;
  if (name in PLACEMENT_FORMATS) return name as keyof typeof PLACEMENT_FORMATS;
  return LEGACY_FORMATS[name] ?? null;
}

export type PlacementFormat = keyof typeof PLACEMENT_FORMATS;

/** Where a slot renders: a page kind for page routes, `module:<type>` for
 *  content detail routes. Exactly the ingest's accepted set. */
export type SlotTemplate =
  | "home"
  | "marketing"
  | "listing"
  | "editorial"
  | "legal"
  | "module:ott"
  | "module:course"
  | "module:event"
  | "module:podcast"
  | "module:post";

export interface StaticSlot {
  /** `^[a-z][a-z0-9_]{0,63}$`, unique, never `custom_*` (that namespace
   *  belongs to slots the page editor declares per page). */
  name: string;
  /** What the admin sees in the booking UI — written for a salesperson. */
  label: string;
  format: PlacementFormat;
  /** Non-empty. The backend re-checks slot ∈ templates at decision time. */
  templates: SlotTemplate[];
}

/**
 * The catalog — one rule, plain names (docs/project/decisions.md D30, P9–P12):
 * every page type declares the same three positions, top / right /
 * bottom, named `<page>_<position>`, labelled "Page — position, where it
 * sits". Same names and shapes on every page type, so one creative fits the
 * same position anywhere and a salesperson needs one sentence. The theme
 * declares all of it; a portal books what it sells and an unbooked slot
 * renders nothing. Extras a page wants (an agencies strip, service tiles) are
 * editor sections named by the author, not catalog rows.
 *
 * Page types and their render sites — keep this list and the call sites in step:
 *
 * - home    — app/[lang]/(public)/page.tsx. `home_top` below the opening
 *             section (after block 0: a hero band or the featured block; the
 *             one eager placement), `home_right` floating in the gutter from
 *             laptops up, `home_bottom` below the body.
 * - news    — communicationPosts, template kind `editorial`: the news INDEX
 *             (lib/pages/listingRoute.tsx, the NewsList page) and every article
 *             (app/[lang]/(public)/news/[slug]/page.tsx). One booking covers the
 *             section: `news_top` above the list / below an article's intro,
 *             `news_right` floating beside the list / the text, `news_bottom`
 *             below the list / the text.
 * - content — every module detail (course, ott, event, podcast, and Članki =
 *             `module:post`): `content_top` at the top of the content column,
 *             `content_right` in the right column under the start card
 *             (components/placements/ColumnPlacement.tsx; on the Članek page that
 *             column is the reading sidebar, from laptops up — D35),
 *             `content_bottom` below the content.
 * - listing — the other index pages (courses, events, otts, podcasts; and the
 *             Članki / authors / organizations indexes, which are `editorial`
 *             kind — hence both templates): `listing_top` above the results,
 *             `listing_right` floating, `listing_bottom` below the results.
 * - academy — the academy landing page (kind `marketing`): `academy_top` below
 *             the hero, `academy_right` floating, `academy_bottom` above the
 *             footer.
 *
 * Shapes (P11): tops are the kit's 970×250 billboard, and so are the home and
 * academy bottoms (the two landing pages); every other bottom is the 728×90
 * leaderboard; rights are the 300×600 half page. Declared in the rebuilt enum
 * — see the HOLD note on PLACEMENT_FORMATS.
 *
 * Renaming a slot orphans its bookings; the 2026-09-05 rename (`article_*` →
 * `news_*`, `detail_bottom` → `content_bottom`, `floating_side` → the five
 * `*_right`) happened before the first production booking.
 *
 * scripts/check-invariants.mjs (`slot-rendered`) fails the build for an entry
 * here that no route or template references by name.
 */
const MODULES: SlotTemplate[] = [
  "module:course",
  "module:ott",
  "module:event",
  "module:podcast",
  "module:post",
];

export const STATIC_SLOTS: readonly StaticSlot[] = [
  // home
  {
    name: "home_top",
    label: "Home — top, below the opening section",
    format: "BILLBOARD",
    templates: ["home"],
  },
  {
    name: "home_right",
    label: "Home — right, beside the content",
    format: "HALF_PAGE",
    templates: ["home"],
  },
  {
    name: "home_bottom",
    label: "Home — bottom, below the content",
    format: "BILLBOARD",
    templates: ["home"],
  },
  // news: the index and the articles
  {
    name: "news_top",
    label: "News — top, above the news list and below an article's intro",
    format: "BILLBOARD",
    templates: ["editorial"],
  },
  {
    name: "news_right",
    label: "News — right, beside the news list and the text",
    format: "HALF_PAGE",
    templates: ["editorial"],
  },
  {
    name: "news_bottom",
    label: "News — bottom, below the news list and the text",
    format: "LEADERBOARD",
    templates: ["editorial"],
  },
  // content pages: every module detail
  {
    name: "content_top",
    label: "Content — top, below the intro",
    format: "BILLBOARD",
    templates: MODULES,
  },
  {
    name: "content_right",
    label: "Content — right, under the card",
    format: "HALF_PAGE",
    templates: MODULES,
  },
  {
    name: "content_bottom",
    label: "Content — bottom, below the content",
    format: "LEADERBOARD",
    templates: MODULES,
  },
  // the other index pages
  {
    name: "listing_top",
    label: "Listings — top, above the results",
    format: "BILLBOARD",
    templates: ["listing", "editorial"],
  },
  {
    name: "listing_right",
    label: "Listings — right, beside the results",
    format: "HALF_PAGE",
    templates: ["listing", "editorial"],
  },
  {
    name: "listing_bottom",
    label: "Listings — bottom, below the results",
    format: "LEADERBOARD",
    templates: ["listing", "editorial"],
  },
  // the academy landing page
  {
    name: "academy_top",
    label: "Academy — top, below the hero",
    format: "BILLBOARD",
    templates: ["marketing"],
  },
  {
    name: "academy_right",
    label: "Academy — right, beside the content",
    format: "HALF_PAGE",
    templates: ["marketing"],
  },
  {
    name: "academy_bottom",
    label: "Academy — bottom, above the footer",
    format: "BILLBOARD",
    templates: ["marketing"],
  },
];

/** The ingest's name rule, mirrored so a bad slot fails at build time (and in
 *  the unit test), not as a 422 on the pipeline's push step. */
export const SLOT_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

const TEMPLATES = new Set<string>([
  "home",
  "marketing",
  "listing",
  "editorial",
  "legal",
  "module:ott",
  "module:course",
  "module:event",
  "module:podcast",
  "module:post",
]);

/**
 * Slot-name rules, mirrored ONCE from the backend (`ThemeAdSlot` + the
 * `adDecisions(slots:)` argument rules) and shared by the page editor
 * (lib/pages/ad-slots.ts), the request (lib/placements/load.ts) and the manifest
 * builder:
 *
 * - static: `SLOT_NAME_RE`, 1–64 chars, letter first;
 * - custom: `custom_` + 1–57 chars of the same charset = 8–64 chars in all. Both
 *   ceilings are the backend's 64; the editor derives and clamps to them.
 * - a request carries at most 20 names of at most 64 chars each, and ONE
 *   offender fails the WHOLE query (`validation`, `data: null`) — so the request
 *   is filtered here, never sent hopefully.
 */
export const CUSTOM_SLOT_PREFIX = "custom_";
export const CUSTOM_SLOT_RE = /^custom_[a-z0-9_]{1,57}$/;
export const MAX_SLOT_NAME = 64;
export const MAX_SLOTS_PER_REQUEST = 20;
/** The most static slots one page DOCUMENT requests beside its own custom
 *  slots — home (or the academy page), with a top, a bottom and
 *  `floating_side`. Keep in step with the render sites above. Routes that are not page documents (the
 *  content details) carry no custom slots and do not count here. */
export const MAX_STATIC_SLOTS_PER_PAGE = 3;
/** Custom slots one page may declare: the request carries the template's own
 *  static slots in the same list of 20, so the editor stops that many short.
 *  (The backend's `PageDocInput.adSlots` cap is also 20; this is the tighter
 *  one.) */
export const MAX_CUSTOM_SLOTS_PER_PAGE = MAX_SLOTS_PER_REQUEST - MAX_STATIC_SLOTS_PER_PAGE;

/** A name the ingest could have accepted for either kind of slot. */
export function isSlotName(name: string): boolean {
  return SLOT_NAME_RE.test(name) || CUSTOM_SLOT_RE.test(name);
}

/**
 * The list as the request may carry it: de-duplicated (a repeated name yields
 * one decision anyway), only names that could be booked, and no more than the
 * cap — order kept, so callers list the template's static slots first and a
 * long custom list loses its tail, not the sold static slot. `dropped` is what
 * did not make it, for the dev-mode warning.
 */
export function requestableSlots(slots: readonly string[]): {
  slots: string[];
  dropped: string[];
} {
  const out: string[] = [];
  const dropped: string[] = [];
  const seen = new Set<string>();
  for (const name of slots) {
    if (seen.has(name)) continue;
    seen.add(name);
    if (isSlotName(name) && out.length < MAX_SLOTS_PER_REQUEST) out.push(name);
    else dropped.push(name);
  }
  return { slots: out, dropped };
}

/** Every way the ingest would refuse the block, as human-readable lines.
 *  Empty = valid. Runs over plain objects so the manifest builder can call it
 *  on the JSON it is about to send. */
// The ingest's rule for `adSlots.*.label` (ThemeIngestController, `max:191`) —
// not a column width. A longer label is a 422 at push time, so refuse it here.
const LABEL_MAX = 191;

export function validateStaticSlots(slots: readonly StaticSlot[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();
  slots.forEach((s, i) => {
    const at = `adSlots[${i}] (${s.name || "?"})`;
    if (!SLOT_NAME_RE.test(s.name)) errors.push(`${at}: name must match ${SLOT_NAME_RE}`);
    if (s.name.startsWith("custom_"))
      errors.push(`${at}: the custom_* namespace belongs to the page editor`);
    if (seen.has(s.name.toLowerCase())) errors.push(`${at}: duplicate name`);
    seen.add(s.name.toLowerCase());
    if (s.label.length > LABEL_MAX) errors.push(`${at}: label over ${LABEL_MAX} characters`);
    if (!(s.format in PLACEMENT_FORMATS))
      errors.push(`${at}: unknown format ${JSON.stringify(s.format)}`);
    if (!Array.isArray(s.templates) || s.templates.length === 0)
      errors.push(`${at}: templates must be a non-empty list`);
    else
      for (const t of s.templates)
        if (!TEMPLATES.has(t)) errors.push(`${at}: unknown template ${JSON.stringify(t)}`);
  });
  return errors;
}

/** `"8:1"` → `"8 / 1"` for CSS `aspect-ratio`. Falls back to the format's own
 *  box when the string is not `w:h` (legacy names resolve), and to 8:1 when
 *  that is unknown too. */
export function aspectRatioCss(ratio: string | null | undefined, format?: string): string {
  const m = /^(\d+):(\d+)$/.exec(ratio ?? "");
  if (m && Number(m[1]) > 0 && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
  const [w, h] = PLACEMENT_FORMATS[canonicalFormat(format) ?? "LEADERBOARD"];
  return `${w} / ${h}`;
}

/**
 * The widest a creative box should grow, in CSS pixels — the layout decides
 * below that. Derived from the box's shape, not the format's NAME: the
 * decision's `aspectRatio` is the contract ("so you need not hard-code the
 * table"), while the enum has already answered with a name the snapshot does
 * not know (`MEDIUM_RECTANGLE` on dev, 2026-09-05) — and an unknown name must
 * not turn a rectangle into a column-wide billboard. The caps are the legacy
 * units' pixel sizes with modest headroom, so a 300×250 or 320×50 creative is
 * not blown up to 970 px and blurred:
 *
 * - strips (w ≥ 4h — the 8:1 leaderboard, the 6:1 mobile strip): 970;
 * - tall units (h ≥ 1.5w — the 1:2 half page): 300;
 * - everything between (6:5 rectangle, 1:1 square): 420.
 */
export function frameMaxWidthPx(ratio: string | null | undefined, format?: string): number {
  const m = /^(\d+)\s*\/\s*(\d+)$|^(\d+):(\d+)$/.exec((ratio ?? "").trim());
  let w: number | undefined;
  let h: number | undefined;
  if (m) {
    w = Number(m[1] ?? m[3]);
    h = Number(m[2] ?? m[4]);
  } else {
    const f = canonicalFormat(format);
    if (f) [w, h] = PLACEMENT_FORMATS[f];
  }
  if (!w || !h) return 970;
  if (w >= 4 * h) return 970;
  if (h >= 1.5 * w) return 300;
  return 420;
}
